import { useEffect, useState } from "react";
import { Section, Eyebrow, H2, Lead, GlassCard, Reveal } from "../components/site/Primitives";
import { Award, Target, Flame, Heart } from "lucide-react";
import api from "../lib/api";
import { log } from "../lib/logger";

export default function About() {
  const [founder, setFounder] = useState(null);
  useEffect(() => { api.get("/content/founder").then((r) => setFounder(r.data)).catch((e) => log.warn("founder content fetch failed", e?.message)); }, []);
  return (
    <div>
      <Section className="pt-40">
        <Eyebrow>About 220 Inc.</Eyebrow>
        <H2 className="max-w-4xl">A veteran-owned holding company building infrastructure, opportunity, and dignity.</H2>
        <Lead className="mt-6">
          220 Inc. is an integrated platform spanning construction, real estate development, property
          management, support services, education, nightlife, transportation, technology, and
          veteran-focused initiatives. Every division shares one mandate: build assets and services
          that make communities measurably stronger.
        </Lead>
      </Section>

      <Section className="border-t border-white/5">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <Reveal>
            <GlassCard className="p-10 h-full" data-testid="founder-card">
              {founder?.image ? (
                <img src={founder.image} alt="Founder" className="w-full aspect-[4/3] object-cover mb-8 border border-white/10" />
              ) : (
                <div className="text-gold mb-6"><Award size={28} /></div>
              )}
              <div className="font-display text-3xl mb-4">{founder?.title || "Army Veteran Founder, 220 Inc."}</div>
              {founder?.subtitle && (
                <div className="text-xs uppercase tracking-[0.2em] text-gold mb-6">{founder.subtitle}</div>
              )}
              <p className="text-white/70 leading-relaxed whitespace-pre-line">
                {founder?.body || "A U.S. Army veteran and lifelong builder, our founder leads 220 Inc. with the same discipline, planning rigor, and commitment to people that defined his service."}
              </p>
              <p className="text-white/40 leading-relaxed mt-4 text-xs">
                Editable from the admin panel.
              </p>
            </GlassCard>
          </Reveal>

          <div className="space-y-6">
            {[
              { icon: <Target size={22} />, title: "Mission-Driven Entrepreneurship", body: "We build companies that serve people first — and produce returns precisely because they do." },
              { icon: <Heart size={22} />, title: "Rebuilding Communities", body: "Our home base — Columbus, GA and Phenix City, AL — anchors a 20-year master plan for inclusive regional growth." },
              { icon: <Flame size={22} />, title: "Job & Housing Pipeline", body: "Education → Certification → Placement → Housing. A closed loop that turns potential into permanence." },
            ].map((c) => (
              <Reveal key={c.title}>
                <GlassCard className="p-6">
                  <div className="text-gold mb-3">{c.icon}</div>
                  <div className="font-display text-xl mb-2">{c.title}</div>
                  <p className="text-white/65 text-sm leading-relaxed">{c.body}</p>
                </GlassCard>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      <Section className="border-t border-white/5">
        <Eyebrow>What We Do</Eyebrow>
        <H2 className="max-w-3xl">Nine disciplines, one operating system.</H2>
        <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {[
            "Construction",
            "Real Estate Development",
            "Property Management",
            "Support Services",
            "Education & Certifications",
            "Nightlife & Entertainment",
            "Transportation",
            "Technology Integration",
            "Veteran-Focused Initiatives",
          ].map((d) => (
            <div key={d} className="bg-[#0c0c0e] p-8">
              <div className="text-xs uppercase tracking-[0.2em] text-gold mb-3">Discipline</div>
              <div className="font-display text-xl">{d}</div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}
